import { RecordState } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Path, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Combobox } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Form, FormFieldItem } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { getLocationsQueryOptions } from '@/queries/locations';

const formSchema = z.object({
  locationId: z.string({ message: 'Required' }),
});

function BulkUpdateAssetLocationDialog({
  open,
  onOpenChange,
  callback,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callback: (locationId: string, resetForm: () => void) => void;
  loading: boolean;
}) {
  const { data: locationsData } = useQuery(getLocationsQueryOptions({ filters: { state: RecordState.Active } }));
  const locations = locationsData?.data;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      locationId: undefined,
    },
  });

  function handleSubmission(data: z.infer<typeof formSchema>) {
    callback(data.locationId, () => form.reset());
    onOpenChange(false);
  }

  function handleOpenChange(open: boolean) {
    onOpenChange(open);
    if (!open) {
      form.reset();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex flex-col gap-[10px]">
        <DialogTitle>Edit Asset Location</DialogTitle>

        <DialogDescription>Bulk editing will change the location of all the assets you selected.</DialogDescription>

        <Form {...form}>
          <form className="mt-2 flex h-full flex-col" onSubmit={form.handleSubmit(handleSubmission)}>
            <FormFieldItem<z.infer<typeof formSchema>, Path<z.infer<typeof formSchema>>>
              control={form.control}
              name="locationId"
              label="Asset Location"
              labelSize="small">
              {({ value, onChange, error }) => (
                <Combobox
                  aria-label="Asset Location"
                  value={value || ''}
                  onChange={onChange}
                  options={locations?.map(({ id, name }) => ({ label: name, value: id })) || []}
                  className={cn('w-full', error && 'border-default-red')}
                />
              )}
            </FormFieldItem>

            <div className="mt-4">
              <Button variant="success" type="submit" disabled={loading}>
                Save
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export { BulkUpdateAssetLocationDialog };
